<template>
  <div class="flex flex-col w-full h-full">
    <div class="flex flex-col h-full p-2 bg-white divide-gray-200 rounded-lg dark:bg-dark-box-background">
      <div :class="[` mb-1 w-100  -pb-0.5 dark:border-dark-divider-border`]">
        <span class="text-lg font-bold text-light-body-text dark:text-dark-body-text">{{ translatedLabel }}</span>
        <div class="flex items-center ml-auto">
          <button v-show="showClearAll" class="mr-2 action-btn dark:bg-transparent dark:text-white whitespace-nowrap" @click="resetFilters">
            {{ $t("Clear all") }}
          </button>
          <div v-if="!disableAdd" class="relative self-stretch">
            <div class="flex p-1 rounded-lg group dark:text-white">
              <button
                type="button"
                class="flex focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus:outline-none focus-visible:ring-offset-gray-100"
                :tabindex="is_simple_filter_on ? '0' : '-1'"
                @click="handleSimpleClick"
                @keydown.arrow-left="handleSimpleClick"
                @keydown.arrow-right="handleSimpleClick">
                <transition
                  mode="out-in"
                  enter-active-class="transition duration-100 ease-in"
                  enter-from-class="opacity-0"
                  enter-to-class="opacity-100">
                  <span
                    :class="[
                      `p-1.5 lg:pl-2.5 lg:pr-3.5 flex items-center text-sm font-medium  ${
                        is_simple_filter_on ? 'text-light-brand-blue border-b-2 border-light-brand-blue' : ''
                      }`
                    ]">
                    <span class="sr-only lg:not-sr-only">{{ $t("Simple") }}</span>
                  </span>
                </transition>
              </button>
              <button
                type="button"
                class="flex rounded-md focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus:outline-none focus-visible:ring-offset-gray-100"
                :tabindex="!is_simple_filter_on ? '0' : '-1'"
                @click="handleAdvancedClick"
                @keydown.arrow-left="handleAdvancedClick"
                @keydown.arrow-right="handleAdvancedClick">
                <transition
                  mode="out-in"
                  enter-active-class="transition duration-100 ease-in"
                  enter-from-class="opacity-0"
                  enter-to-class="opacity-100">
                  <span
                    :class="[
                      `p-1.5 lg:pl-2.5 lg:pr-3.5  flex items-center text-sm font-medium ${
                        !is_simple_filter_on ? 'text-light-brand-blue border-b-2 border-light-brand-blue' : ''
                      }`
                    ]">
                    <span class="sr-only lg:not-sr-only">{{ $t("Advanced") }}</span>
                  </span>
                </transition>
              </button>
            </div>
          </div>
        </div>
      </div>

      <ul :class="[`${!is_simple_filter_on ? 'mb-5' : ''} relative min-w-full`]">
        <draggable
          v-model="filter"
          v-bind="dragOptions"
          :disabled="true"
          class="min-w-full"
          item-key="name"
          @start="drag = true"
          @end="drag = false">
          <template #item="{ element, index }">
            <div v-if="element.item_type != 'clause' && element.item_type != 'operator'" class="relative">
              <div class="relative flex items-center justify-center min-w-full">
                <div
                  class="flex-1 min-w-full bg-white rounded-md dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border">
                  <div class="min-w-full">
                    <div v-if="!metric" class="relative items-center w-full px-1 py-2 text-sm font-medium rounded-t-md focus:outline-none">
                      <AutoCompleteInput
                        v-if="$filter($store.state.metrics, { object_type: objectType }).length"
                        :disabled="disabled"
                        :icon="undefined"
                        :placeholder="$t('Search for Metric')"
                        :data="$filter(all_metrics, { object_type: objectType })"
                        :is-box-background="isBoxBackground"
                        :value="element.name"
                        @chosen="chooseMetric($event, index, element)"
                        @reset="resetMetric(index)" />
                    </div>
                    <Listbox
                      v-if="element.name && other_metrics.find((metric) => element.reference_name?.includes(metric.reference_name))"
                      v-model="element.option"
                      :disabled="disabled"
                      as="div"
                      class="relative z-20 flex-1">
                      <div class="relative h-full">
                        <ListboxButton
                          class="relative z-20 w-full h-full pl-3 pr-10 overflow-hidden text-center bg-white border rounded-md cursor-default focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
                          <span v-if="element.option" class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{
                            element.option?.label
                          }}</span>
                          <span v-else class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">Field</span>
                          <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </ListboxButton>

                        <transition
                          leave-active-class="transition duration-100 ease-in"
                          leave-from-class="opacity-100"
                          leave-to-class="opacity-0">
                          <ListboxOptions
                            class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
                            <ListboxOption
                              v-for="option in element.options"
                              :key="option.value"
                              v-slot="{ active, selected }"
                              as="template"
                              :value="option"
                              @click="metricSubNameChanged(element, option, index)">
                              <li
                                :class="[
                                  active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                                  'cursor-default relative py-2 pl-5 pr-4 text-center has-tooltip'
                                ]">
                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                                  {{ option?.label }}
                                </span>

                                <span
                                  v-if="selected"
                                  :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                                  <Check class="w-5 h-5" aria-hidden="true" />
                                </span>
                              </li>
                            </ListboxOption>
                          </ListboxOptions>
                        </transition>
                      </div>
                    </Listbox>
                    <Listbox
                      v-if="element.name"
                      v-model="element.operator"
                      :disabled="disabled"
                      as="div"
                      class="relative z-30 flex-1 w-full mb-2">
                      <div class="relative w-full h-full">
                        <ListboxButton
                          class="relative z-20 w-full h-full py-1 pl-3 pr-10 overflow-hidden bg-white border rounded-md cursor-default text-start focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
                          <span
                            v-if="element.operator"
                            class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{ element.operator?.label }}</span>
                          <span v-else class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{
                            $t("Condition")
                          }}</span>
                          <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </ListboxButton>

                        <transition
                          leave-active-class="transition duration-100 ease-in"
                          leave-from-class="opacity-100"
                          leave-to-class="opacity-0">
                          <ListboxOptions
                            class="absolute z-20 mt-1 w-[100%] overflow-y-auto overflow-x-hidden bg-white rounded-md shadow-lg dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
                            <ListboxOption
                              v-for="operator in element.operators"
                              :key="operator.value"
                              v-slot="{ active, selected }"
                              as="template"
                              :value="operator"
                              @click="metricOperatorChanged(element)">
                              <li
                                :class="[
                                  active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                                  'cursor-default relative py-2 pl-5 pr-1 text-start has-tooltip'
                                ]">
                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                                  {{ operator.label }}
                                </span>

                                <span
                                  v-if="selected"
                                  :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 -left-1 flex items-center pl-1.5']">
                                  <Check class="w-5 h-5" aria-hidden="true" />
                                </span>
                              </li>
                            </ListboxOption>
                          </ListboxOptions>
                        </transition>
                      </div>
                    </Listbox>

                    <div
                      v-if="(!disableAdd && filter.length == index + 1) || filter[index + 1]?.item_type == 'operator'"
                      class="absolute w-full text-2xl text-center cursor-pointer -bottom-5 text-light-brand-blue"
                      @click="addMetricToFilter($filter(all_metrics, { object_type: objectType })[0], index)">
                      <span class="p-2 py-0 border rounded-full border-light-brand-blue">+</span>
                    </div>
                    <div
                      v-if="element?.operator?.value !== '__ISNULL__' && element?.operator?.value !== '__IS_NOT_NULL__' && element.name"
                      class="flex w-full rounded-md">
                      <!-- operators -->

                      <QuarterDatePicker
                        v-if="element?.operator?.value == '__DAYS_WITHIN_RANGE__'"
                        v-model:current-range="element.value"
                        class="w-full mr-2 bg-white rounded-lg dark:bg-dark-box-background" />

                      <input
                        v-else-if="
                          (element.data_type == 'Date' || element.data_type == 'DateTime') && element?.operator?.value.includes('__')
                        "
                        v-model="element.value"
                        :disabled="disabled"
                        placeholder="Number of days"
                        min="1"
                        type="number"
                        class="flex-1 block w-full pl-5 my-2 overflow-hidden bg-white rounded-md border-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border"
                        @input="
                          (e) =>
                            $utils.debounce(() => {
                              e.target.value < 1 ? (element.value = '') : element.value
                            }, 600)
                        ">

                      <DatePicker
                        v-else-if="element.data_type == 'Date' || element.data_type == 'DateTime'"
                        :value="element.value"
                        :is-dark="$store.state.ui.darkMode"
                        :model-config="{
                          type: 'string',
                          mask: 'YYYY-MM-DD'
                        }"
                        class="w-full px-1 pr-2 overflow-hidden bg-white dark:bg-dark-screen-background"
                        :popover="{ visibility: 'click' }"
                        @dayclick="($event) => handleChangeDate($event, element)">
                        <template #default="{ inputEvents }">
                          <input
                            :value="element.value"
                            :disabled="disabled"
                            class="w-full py-2 bg-white border rounded outline-none dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:ring-blue-light focus:border-blue-light"
                            v-on="inputEvents">
                        </template>
                      </DatePicker>

                      <input
                        v-else-if="element.data_type == 'Time'"
                        v-model="element.value"
                        :disabled="disabled"
                        type="time"
                        class="flex-grow w-full pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackground
                            ? 'bg-light-screen-background dark:bg-dark-screen-background'
                            : 'bg-light-box-background dark:bg-dark-box-background'
                        ]">
                      <RecordAutoCompleteInput
                        v-else-if="element.reference_name == 'parent__id' || element.reference_name == 'parent__name'"
                        :filter-metric-ids="[$utils.getMetricByReferenceName($utils.getOptionMetricReference('company', 'name')).id]"
                        :is-async="true"
                        :placeholder="$t('Type a word or a sentence')"
                        :label="''"
                        :is-box-background="true"
                        :disabled="
                          $route.name == 'CompanyProfile' || ($route.name == 'ObjectProfile' && $route.params.object_type == 'opportunity')
                        "
                        name="parent__name"
                        type="text"
                        label-attr="name"
                        value-attr="id"
                        classes="w-full block border-none p-0"
                        :value="element.value"
                        @chosen="
                          ($event) => {
                            element.value = $event.id
                            element.reference_name = 'parent__id'
                          }
                        "
                        @changed="
                          ($event) => {
                            if (!$event?.id) {
                              element.reference_name = 'parent__name'
                              element.value = $event
                            } else {
                              element.reference_name = 'parent__id'
                              element.value = $event.id
                            }
                          }
                        " />
                      <input
                        v-else-if="element.data_type == 'Number'"
                        v-model="element.value"
                        :disabled="disabled"
                        placeholder="Type a number"
                        type="number"
                        class="flex-1 block w-full pl-5 my-2 overflow-hidden bg-white rounded-md border-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border">
                      <input
                        v-else-if="element.data_type == 'Boolean'"
                        v-model="element.value"
                        :disabled="disabled"
                        :placeholder="$t('Type yes or no')"
                        type="text"
                        class="w-full py-2 my-2 bg-white border rounded outline-none dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:ring-blue-light focus:border-blue-light">
                      <ComboBoxInput
                        v-else-if="element.reference_name == 'tags__name'"
                        v-model:model-value="element.value"
                        name="tags"
                        :items="$store.state.data.tags"
                        label-attr="name"
                        value-attr="name"
                        :is-multiple="false"
                        :allow-null="true"
                        :return-value="true"
                        label=""
                        classes="w-full relative dark:text-white"
                        input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                        selection-text=""
                        :placeholder="$t('Assigned tags')"
                        :floating-label="false"
                        :show-label="false" />
                      <ComboBoxInput
                        v-else-if="
                          element.reference_name == 'owner__email' ||
                            element.reference_name == 'co_owners__email' ||
                            (element.reference_name.includes('_email') &&
                              other_metrics.find((metric) => element.reference_name.includes(metric.reference_name)))
                        "
                        v-model:model-value="element.value"
                        name="owner"
                        :items="$store.state.data.users"
                        classes="w-full relative dark:text-white"
                        label-attr="full_name"
                        value-attr="email"
                        label=""
                        :placeholder="$t('Team members')"
                        :show-label="false"
                        :floating-label="false"
                        :allow-null="false">
                        <template #item="{ item, active }">
                          <li
                            :class="[
                              active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white',
                              'cursor-default relative py-2 pl-4 pr-4 group/name'
                            ]">
                            <div class="flex items-center">
                              <img
                                v-if="item.avatar"
                                class="inline-block rounded-full h-9 w-9 object-cover"
                                :src="item.avatar"
                                onerror="this.onerror=null; this.src=window.default_avatar;"
                                alt="">
                              <Avatar
                                v-else
                                :hover-effect="true"
                                :first-name="item.first_name"
                                :last-name="item.last_name"
                                class="group-hover/name:bg-cyan-500" />
                              <div class="ml-3">
                                <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                                  {{ Record.from(item).full_name }}
                                </p>
                                {{ item.email }}
                              </div>
                            </div>
                          </li>
                        </template>
                      </ComboBoxInput>

                      <ComboBoxInput
                        v-else-if="element.reference_name == 'owner__id' || element.reference_name == 'co_owners__id'"
                        v-model:model-value="element.value"
                        name="owner"
                        :items="$store.state.data.users"
                        classes="w-full relative dark:text-white"
                        label-attr="full_name"
                        value-attr="id"
                        label=""
                        :placeholder="$t('Team members')"
                        :show-label="false"
                        :floating-label="false"
                        :allow-null="false">
                        <template #item="{ item, active }">
                          <li
                            :class="[
                              active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white',
                              'cursor-default relative py-2 pl-4 pr-4 group/name'
                            ]">
                            <div class="flex items-center">
                              <img
                                v-if="item.avatar"
                                class="inline-block rounded-full h-9 w-9 object-cover"
                                :src="item.avatar"
                                onerror="this.onerror=null; this.src=window.default_avatar;"
                                alt="">
                              <Avatar
                                v-else
                                :hover-effect="true"
                                :first-name="item.first_name"
                                :last-name="item.last_name"
                                class="group-hover/name:bg-cyan-500" />
                              <div class="ml-3">
                                <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                                  {{ Record.from(item).full_name }}
                                </p>
                                {{ item.email }}
                              </div>
                            </div>
                          </li>
                        </template>
                      </ComboBoxInput>
                      <ComboBoxInput
                        v-else-if="
                          element?.option?.options && other_metrics.find((metric) => element.reference_name.includes(metric.reference_name))
                        "
                        v-model:model-value="element.value"
                        name="options"
                        :items="element.option.options"
                        classes="w-full relative dark:text-white"
                        label-attr="label"
                        value-attr="value"
                        label=""
                        :placeholder="$t('Select item')"
                        :show-label="false"
                        :floating-label="false"
                        :allow-null="false">
                        <template #item="{ item, active }">
                          <li
                            :class="[
                              active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white',
                              'cursor-default relative py-2 pl-4 pr-4 group/name'
                            ]">
                            <div class="flex items-center">
                              <div class="ml-3">
                                <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                                  {{ item.label }}
                                </p>
                              </div>
                            </div>
                          </li>
                        </template>
                      </ComboBoxInput>
                      <AutoCompleteInput
                        v-else
                        v-model="element.value"
                        class="w-full bg-transparent border-none"
                        :value="element.value"
                        :icon="undefined"
                        :is-async="element.input_type == 'Select' && element.input_options?.choices.length > 0 ? false : true"
                        :data="
                          element.input_type == 'Select' && element.input_options?.choices.length > 0
                            ? element.input_options?.choices
                            : element.suggestions
                        "
                        :endpoint-async-url="getMetricSuggestionUrl(element)"
                        :is-box-background="isBoxBackground"
                        :disabled="filter[filter.length - 1]?.item_type == 'operator' || disabled"
                        :placeholder="
                          element?.operator?.value == 'in' || element?.operator?.value == '__EXCLUDES__'
                            ? $t('Type word/s separated by comma')
                            : $t('Type a word or a sentence')
                        "
                        type="text"
                        classes="relative text-light-body-text dark:text-dark-body-text bg-white dark:bg-dark-screen-background rounded-md"
                        input-classes="overflow-hidden  focus:outline-none focus:ring-blue-light focus:border-blue-light  w-full rounded-md border-light-divider-border dark:border-dark-divider-border"
                        @changed="
                          ($event) => {
                            element.value = $event
                          }
                        "
                        @chosen="
                          ($event) => {
                            element.value = $event
                          }
                        " />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="element.item_type == 'operator'" class="relative w-full" :class="{ hidden: is_simple_filter_on }">
              <div class="relative flex items-center justify-center w-full group">
                <div
                  :class="[
                    `min-w-0 pt-10 ${
                      !is_simple_filter_on &&
                      element.item_type === 'operator' &&
                      filter[index + 3] &&
                      filter[index + 3].item_type !== 'operator'
                        ? 'pb-12'
                        : 'pb-2'
                    }`
                  ]">
                  <div class="relative inline-flex w-24 p-2 px-4 mt-2 ml-2 rounded-md">
                    <div class="relative flex items-center w-full text-sm cursor-pointer">
                      <div
                        :class="[
                          `w-full text-center border dark:border-dark-divider-border rounded-l-md px-2 py-1 dark:text-white ${
                            element.operator.label == 'And' ? 'bg-dark-brand-blue text-white' : ''
                          }`
                        ]"
                        @click="setOperator($store.state.data.filter_logic_operators[0], index)">
                        {{ $t("And") }}
                      </div>
                      <div
                        :class="[
                          `w-full text-center  dark:border-dark-divider-border border-r border-t border-b rounded-r-md dark:text-white px-3 py-1 ${
                            element.operator.label == 'Or' ? 'bg-dark-brand-blue text-white' : ''
                          }`
                        ]"
                        @click="setOperator($store.state.data.filter_logic_operators[1], index)">
                        {{ $t("Or") }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-if="filter.length && !disableAdd" #footer>
            <div
              v-if="filter.length && filter[filter.length - 1].item_type != 'operator' && filter[filter.length - 1].item_type != 'clause'"
              class="relative col-span-1 mt-8 border border-none rounded-md"
              :class="{ hidden: is_simple_filter_on }">
              <div
                class="flex items-center justify-center w-full text-sm align-middle cursor-pointer"
                @click="addLogicOperator($store.state.data.filter_logic_operators[0])">
                <div class="relative w-10 h-10 pt-1 mb-2 text-2xl text-center border rounded-full border-light-brand-blue left-4 text-blue">
                  +
                </div>
              </div>
            </div>
          </template>
        </draggable>
      </ul>

      <div
        v-if="showButtons"
        :class="[
          `${is_simple_filter_on ? 'mt-16' : 'mt-0'} flex flex-row -ml-2 ${
            !((filter.length - 1) % 4) &&
            !(
              disabled ||
              filter.length == 1 ||
              filter.filter((x) => x.operator === null).length > 0 ||
              filter.filter((x) => !x.value && x.item_type != 'clause' && x.item_type != 'operator').length > 0
            )
              ? 'mt-24'
              : 'mt-8'
          }`
        ]">
        <div class="flex justify-between w-full gap-1">
          <button
            class="w-20 py-3 bg-white border action-btn border-light-divider-border dark:border-dark-divider-border dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text"
            @click="handleCancel">
            Cancel
          </button>
          <button
            :disabled="disabled || isFilterUnValid"
            class="primary-btn w-40 !disabled:opacity-50 disabled:cursor-default"
            @click="
              () => {
                emitFilter()
              }
            ">
            {{ $t("Apply Filter") }}
          </button>
        </div>
      </div>
      <button
        v-if="showSaveButton"
        class="mt-6 primary-btn w-[95%] ml-[2.5%] whitespace-nowrap py-3 border border-light-divider-border dark:border-dark-divider-border text-white"
        @click="saveSearch">
        {{ $t("SAVE SEARCH") }}
      </button>
    </div>
  </div>
</template>

<script>
import RecordAutoCompleteInput from "@/components/shared/RecordAutoCompleteInput.vue"
import json_data from "@/components/shared/json-files/all-obj-fields.json"
import { DatePicker } from "v-calendar"
import draggable from "vuedraggable"
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue"
import AutoCompleteInput from "@/components/shared/AutoCompleteInput.vue"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import Avatar from "@/components/shared/Avatar.vue"
import { ChevronDown, Check } from "lucide-vue-next"
import { transformMetricFilterToServerFilter, transformServerFilterToMetricFilter } from "@/services/data.transformers"
import { Record } from "@/models/record"
import QuarterDatePicker from "@/components/shared/QuarterDatePicker.vue"

export default {
  components: {
    RecordAutoCompleteInput,
    QuarterDatePicker,
    Avatar,
    DatePicker,
    AutoCompleteInput,
    ComboBoxInput,
    ChevronDown,
    draggable,
    Listbox,
    ListboxOptions,
    ListboxOption,
    ListboxButton,
    Check
  },
  props: {
    label: {
      type: String,
      default: "Filters"
    },
    saveSearch: {
      type: Function,
      required: false
    },
    currentFilter: null,
    disableAdd: {
      type: Boolean,
      default: false
    },
    showSaveButton: {
      type: Boolean,
      default: false
    },
    showClearAll: {
      type: Boolean,
      default: true
    },
    showButtons: {
      type: Boolean,
      default: true
    },
    initialFilter: {
      type: String,
      default: ""
    },

    disabled: {
      type: Boolean,
      default: false
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    objectType: {
      type: String,
      required: false
    },
    metric: {
      type: Object,
      required: false
    }
  },
  emits: ["reset", "cancel", "filter", "change"],

  data() {
    return {
      Record,
      filter: [],
      drag: false,
      is_simple_filter_on: true,
      lock_drag: true,
      other_metrics: [],
      filter_backup: null,
      forecasted_objects: []
    }
  },
  computed: {
    translatedLabel() {
      return this.$t(this.label)
    },
    isFilterUnValid() {
      return (
        this.filter.length == 1 ||
        this.filter.filter((x) => x.operator === null).length > 0 ||
        this.filter.filter(
          (x) =>
            this.$utils.isEmpty(x.value) &&
            x.item_type != "clause" &&
            x.item_type != "operator" &&
            (typeof x.value != "object" || (typeof x.value != "string" && (x.data_type == "Date" || x.data_type == "DateTime")))
        ).length > 0
      )
    },
    all_metrics() {
      let forecast_metrics = []
      if (this.forecasted_objects?.includes(this.objectType)) {
        forecast_metrics = [
          {
            reference_name: `forecasts__forecast_status`,
            name: this.$t("Forecast Status"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["forecasted", "actual"] },
            object_type: this.objectType
          },
          {
            reference_name: `forecasts__forecast_type`,
            name: this.$t("Forecast Type"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["non-renewable", "renewable"] },
            object_type: this.objectType
          },
          {
            reference_name: `forecasts__forecast_value`,
            name: this.$t("Forecast Value"),
            data_type: "Number",
            input_type: "Number",
            object_type: this.objectType
          }
        ]
      }
      let owner_co_owner_filer = [
        { reference_name: `owner__email`, name: this.$t("Owner"), data_type: "String", input_type: "Text", object_type: this.objectType },
        {
          reference_name: `co_owners__email`,
          name: this.$t("Co-Owner"),
          data_type: "String",
          input_type: "Text",
          object_type: this.objectType
        }
      ]
      let tags_filter_fields = [
        {
          reference_name: `tags__name`,
          name: this.$t("Assigned tags"),
          data_type: "String",
          input_type: "Text",
          object_type: this.objectType
        }
      ]
      return this.$store.state.metrics
        .concat(forecast_metrics)
        .concat(owner_co_owner_filer)
        .concat(this.other_metrics)
        .concat(tags_filter_fields)
    },
    dragOptions() {
      return {
        animation: 200,
        group: "metrics filter",
        disabled: this.lock_drag,
        ghostClass: "ghost",
        chosenClass: "chosen"
      }
    }
  },
  watch: {
    currentFilter: {
      deep: true,
      handler() {
        this.initFilter()
      }
    },
    initialFilter: {
      handler() {
        // this.initFilter()
      }
    },
    filter: {
      deep: true,
      handler() {
        if (this.filter.length <= 1) {
          this.addMetricToFilter(this.$filter(this.all_metrics, { object_type: this.objectType })[0])
        }
        let filter = this.filter.map((item) => {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (item.operator?.value == "__DAYS_WITHIN_RANGE__") {
            item["modified_value"] = `('${this.$moment(item.value.start).format("YYYY-MM-DD")}','${this.$moment(item.value.end).format(
              "YYYY-MM-DD"
            )}')`
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator?.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator?.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator?.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator?.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator?.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator?.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator?.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator?.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator?.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator?.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator?.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator?.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          return item
        })

        let parsed_filter = transformMetricFilterToServerFilter(filter)
        this.$emit("change", { filter: parsed_filter.filter, valid: parsed_filter.valid })
      }
    }
  },
  created() {
    this.forecasted_objects = this.$utils
      .find(this.$store.state.settings.modules_settings, { reference_code: "renewal_base_metric" })
      ?.mapped_objects_metrics?.map((type_map) => {
        return this.$store.state.object_types.find((x) => x.id == type_map.object_type)?.type
      })
    Object.entries(json_data).map((item) => {
      this.other_metrics.push({ ...item[1], object_type: this.objectType })
    })
    this.initFilter()
    if (!this.initialFilter) {
      this.addMetricToFilter(this.$filter(this.all_metrics, { object_type: this.objectType })[0])
    }

    if (this.metric && !this.initialFilter) {
      this.chooseMetric(this.metric, 1)
    }
  },

  methods: {
    handleChangeDate(e, element) {
      element.value = e.id
    },
    metricOperatorChanged(metric) {
      if (metric.operator.value == "__ISNULL__") {
        metric.value = "True"
      }

      if (metric.operator.value == "__IS_NOT_NULL__") {
        metric.value = "False"
      }
    },
    metricSubNameChanged(metric, option, index) {
      let arr = metric.reference_name.split("__")
      metric.reference_name = `${arr[0] + "__"}`.concat(option.value)
      metric.data_type = option.data_type
      metric.input_type = option.input_type
      this.chooseMetric(metric, index)
    },
    handleCancel() {
      this.filter = [{ item_type: "clause", clause: { label: "All", value: "(and", inner_operator: "and" } }]
      this.$emit("cancel")
      setTimeout(() => {
        this.addMetricToFilter(this.$filter(this.all_metrics, { object_type: this.objectType })[0])
      }, 10)
    },
    handleSimpleClick() {
      this.is_simple_filter_on = true
      this.filter_backup = this.filter
      const indexes_to_remove = []
      this.filter.forEach((f, i) => {
        if (i) {
          if (f.item_type === "clause" || f.item_type === "operator") {
            indexes_to_remove.push(i)
          }
        }
      })

      this.filter = this.filter.filter((ff, j) => !indexes_to_remove.includes(j))
    },

    handleAdvancedClick() {
      this.is_simple_filter_on = false
      if (this.filter_backup) {
        this.filter = this.filter_backup
      }
    },
    emitFilter() {
      const filter_cleaned = []
      this.filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({ ...item, value: item.value == null ? "" : item.value.trim() })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })

      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])
      this.$emit("filter", parsed_filter.filter)
    },
    getMetricSuggestionUrl(metric) {
      return `/api/organizations/${this.$store.state.organization.id}/metrics/${metric.id}/suggestions/`
    },

    addMetricToFilter(in_metric, index) {
      let metric = { ...in_metric }
      metric = this.injectOperators(metric)

      if (index !== undefined) {
        this.filter.splice(index + 1, 0, { ...metric, name: "" })
      } else {
        this.filter.push({ ...metric, name: "" })
      }
    },

    setOperator(operator, index) {
      this.filter.splice(index, 1, { ...this.filter[index], operator })
    },

    chooseMetric(e, index) {
      let metric = { ...e }
      metric = this.injectOperators(metric)
      this.filter.splice(index, 1, metric)
    },

    resetFilters() {
      this.filter = []
      this.initFilter()
      this.addMetricToFilter(this.$filter(this.all_metrics, { object_type: this.objectType })[0])
    },
    resetMetric(index) {
      this.filter.splice(index, 1, { ...this.filter, name: "" })
    },

    injectOperators(metric) {
      let type = metric.data_type?.toLowerCase()
      if (type == "datetime" || type == "date") {
        type = "date"
      }
      let value = ""
      // For boolean the value is allways true, but the operator
      // can be == or != ie: == true or != true which is false
      if (type == "boolean") {
        value = "true"
      }
      metric["operators"] = this.$store.state.data.filter_operators.filter((o) => o.for.split("_").findIndex((t) => t == type) != -1)
      metric["operator"] = null
      metric["value"] = value
      metric["item_type"] = "metric"
      return metric
    },
    addLogicOperator(operator) {
      this.filter.push({ item_type: "operator", operator })
      this.filter.push({ item_type: "clause", clause: this.$store.state.data.filter_logic_clauses[0] })
      this.addMetricToFilter(this.$filter(this.all_metrics, { object_type: this.objectType })[0])
    },

    initFilter() {
      if (this.initialFilter) {
        let filter = transformServerFilterToMetricFilter(this.initialFilter)
        //change logic to know if its simple or not
        this.is_simple_filter_on = filter.is_simple_filter
        this.filter = filter.filter
      } else if (this.currentFilter) {
        let filter = transformServerFilterToMetricFilter(this.currentFilter)
        //change logic to know if its simple or not
        this.is_simple_filter_on = filter.is_simple_filter
        this.filter = filter.filter
      } else {
        this.filter = [{ item_type: "clause", clause: { label: "All", value: "(and", inner_operator: "and" } }]
      }
    }
  }
}
</script>
<style scoped lang="postcss">
.ghost {
  @apply bg-gray-300 border-0 w-full;
}
.chosen {
  @apply bg-transparent border-0;
}
.chosen .line {
  opacity: 0;
}
</style>
